import { Component, Inject, OnInit } from "@angular/core";
import { MD_DIALOG_DATA, MdDialog } from "@angular/material";
import { ProgramArticleService } from "../../services/program-article.service";
import { ProgramArticlePictureETTValue } from "../../models/program-article-translation-value";
import { Router } from "@angular/router";
import { PictureSet, PictureSets } from "../../models/pictureSets";
import {
  AdditionalPicturesActions,
  AdditionalPicturesStateInterface,
} from "../../store/actions/additional-pictures.actions";
import { Store } from "@ngrx/store";
import { SuccessComponent } from "../success/success.component";
import {
  ProgramArticlesActions,
  ProgramArticlesStateInterface,
} from "../../store/actions/program-articles.actions";
import { switchMap } from "rxjs/operators";
import { PictureSetService } from "../../services/pictureSet.service";
import { ProgramArticle } from "../../models/program-article";

@Component({
  selector: "app-additional-pictures-modal",
  templateUrl: "./additional-pictures-modal.component.html",
  styleUrls: ["./additional-pictures-modal.component.scss"],
})
export class AdditionalPicturesModalComponent implements OnInit {
  validatedEmissionId: number;
  channelId: number;
  languageId: number;
  destinationId: number;
  hasReplays: boolean;
  destinationName: string;
  loading: boolean;
  pictureSets: PictureSet[];
  newPictureSetId: number;
  selectablePictureSets: any[];
  availableOptionsPictureSet: any[] = [];
  selectedPictureSets: PictureSet[] = [];
  selectedPictureSetsMap: { [key: number]: boolean } = {};
  selectedSets: number[] = [];
  programArticle: ProgramArticle;

  constructor(
    @Inject(MD_DIALOG_DATA) public data: any,
    private store: Store<any>,
    private router: Router,
    private mdDialog: MdDialog,
    private additionalPicturesActions: AdditionalPicturesActions,
    private programArticleService: ProgramArticleService,
    private programArticlesActions: ProgramArticlesActions,
    private pictureSetService: PictureSetService
  ) {
    this.validatedEmissionId = data.validatedEmissionId;
    this.channelId = data.channelId;
    this.languageId = data.languageId;
    this.destinationName = data.destinationName;
  }

  ngOnInit() {
    this.store
      .select("programArticlesState")
      .subscribe((programArticleState: ProgramArticlesStateInterface) => {
        this.programArticle = programArticleState.currentProgramArticle;
      });

    this.selectablePictureSets =
      JSON.parse(localStorage.getItem("pictureSet")) || [];
    this.getAdditionalPictures();

    this.store
      .select("additionalPicturesState")
      .subscribe((state: AdditionalPicturesStateInterface) => {
        if (state.pictureSets) {
          this.pictureSets = state.pictureSets.pictureSets;
        }
      });
  }

  getAdditionalPictures() {
    this.loading = true;
    this.programArticleService
      .getPictureSets(
        this.validatedEmissionId,
        this.channelId,
        this.languageId,
        this.destinationName
      )
      .subscribe((pictureSets: PictureSets) => {
        this.destinationId = pictureSets.destinationId;
        this.hasReplays = pictureSets.hasReplays;
        this.pictureSets = pictureSets.pictureSets;

        this.selectedPictureSets = this.pictureSets.map((pictureSet) => ({
          setId: pictureSet.setId,
          format: pictureSet.format,
          pictures: pictureSet.pictures,
        }));
        this.additionalPicturesActions.setAdditionalPictures(pictureSets);
        this.loading = false;

        for (const set of this.pictureSets) {
          this.selectedSets.push(set.setId);
        }
      });
  }

  getPictureByFormat(
    pictures: ProgramArticlePictureETTValue[],
    formatName: string
  ) {
    if (pictures && Array.isArray(pictures))
      return pictures.find((ps) => ps.pictureFormatCode === formatName);
    return null;
  }

  toggleLockPicture(pictures: ProgramArticlePictureETTValue[], value: boolean) {
    pictures.forEach((p) => (p.isLocked = value));
  }

  getPictureSourceByFormat(
    pictures: ProgramArticlePictureETTValue[],
    formatName: string
  ) {
    return (
      this.getPictureByFormat(pictures, formatName).pictureURL ||
      this.getPictureByFormat(pictures, formatName).blob
    );
  }

  openImageSetModal(setId: number) {
    if (setId != 0) {
      const format = [];
      format.push(
        this.selectablePictureSets.find((set) => set.id === setId)
          .pictureSetFormats[0].pictureFormatName
      );
      this.router.navigate(
        [
          {
            outlets: {
              modal: [
                "edit",
                "picture",
                this.validatedEmissionId,
                setId,
                this.destinationName,
                "additional-pictures",
              ],
            },
          },
        ],
        {
          queryParams: {
            format: format,
          },
        }
      );
      this.selectedSets.push(setId);
    }
  }
  save(replays: boolean) {
    this.loading = true;
    this.programArticleService
      .uploadAdditionalPictures(
        {
          validatedEmissionId: this.validatedEmissionId,
          channelId: this.channelId,
          languageId: this.languageId,
          destinationId: this.destinationId,
          hasReplays: this.hasReplays,
          pictureSets: this.pictureSets,
        },
        replays
      )
      .pipe(
        switchMap((data) =>
          this.programArticleService.updateProgramArticle(
            data.value,
            replays,
            false
          )
        )
      )
      .subscribe((p) => {
        this.programArticlesActions.updateProgramArticle(p.programArticle);
        this.programArticlesActions.setCurrentProgramArticle(p.programArticle);
        this.close();
        this.mdDialog.open(SuccessComponent, {
          data: { message: "Picture sets have been updated." },
        });
      });
  }

  /**
   * Check if all selected setIds are unique
   * @returns {boolean} - True if all selected setIds are unique, false otherwise
   */
  areSetIdsUnique(): boolean {
    const selectedSetIds = this.pictureSets.map((set) => set.setId);
    const uniqueSetIds = new Set(selectedSetIds);
    const hasNullSetId = selectedSetIds.some(
      (setId) => setId === null || setId.toString() == "0"
    );
    return (
      uniqueSetIds.size === selectedSetIds.length &&
      !hasNullSetId &&
      this.pictureSets.length > 0
    );
  }

  hasSetId(): boolean {
    const hasNullSetId = this.pictureSets.some(
      (pictureSet) => pictureSet.setId.toString() === "0"
    );
    return !hasNullSetId;
  }

  isPictureSetNotEmpty(): boolean {
    if (
      (this.selectedPictureSets && this.selectedPictureSets.length) ||
      (this.pictureSets && this.pictureSets.length)
    )
      return true;
    else return false;
  }

  isDuplicateSetId(rowIndex: number): boolean {
    const currentSetId = this.pictureSets[rowIndex].setId;
    return this.pictureSets.some(
      (set, index) => index !== rowIndex && set.setId === currentSetId
    );
  }

  /**
   * Delete set
   * @param {number} setId
   */
  deleteSet(setId: number) {
    this.additionalPicturesActions.deleteAdditionalPictures(setId);
    const indexToDelete = this.selectedSets.indexOf(setId);
    if (indexToDelete !== -1) {
      this.selectedSets.splice(indexToDelete, 1);
    }
  }

  addRow() {
    this.pictureSets.push({
      setId: 0,
      format:
        this.selectablePictureSets[0].pictureSetFormats[0].pictureFormatName,
      pictures: [],
    });
  }

  isSetSelected(setId: number): boolean {
    return this.selectedSets.includes(setId);
  }

  changeSelectedPictureSet() {
    this.selectedSets.splice(0, this.selectedSets.length);
    for (const set of this.pictureSets) {
      this.selectedSets.push(set.setId);
    }
  }

  check(pictures: ProgramArticlePictureETTValue[]) {
    if (
      !pictures ||
      pictures.length === 0 ||
      !pictures[0] ||
      !pictures[0].pictureURL
    ) {
      return true;
    }
    return false;
  }

  multilingual(pictureSet: PictureSet) {
    this.pictureSetService.setPictureSet(pictureSet);
    this.router.navigate([
      {
        outlets: {
          modal: ["edit", "max-multilingual"],
        },
      },
    ]);
  }

  close() {
    this.mdDialog.closeAll();
  }
}



// WEBPACK FOOTER //
// ./src/app/modals/additional-pictures-modal/additional-pictures-modal.component.ts